import { ChangeDetectionStrategy, Component, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { Store } from '@ngrx/store';
import { AddPublicationChannelComponent } from '@src/suite/common/components/manage-publication-channels/publication-channel-item/add-publication-channel';
import { PSTFieldNames } from '@src/suite/common/data-models/fields/pst-field-names';
import { ObjectTypes } from '@src/suite/common/enums/object-types';
import { ExternalActionsService } from '@src/suite/common/services/external-actions-service';
import { GlobalTablesService } from '@src/suite/common/services/global-tables-service';
import { CastApiActions } from '@src/suite/common/store/cast/cast.actions';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { take } from 'rxjs';

@Component({
    selector: 'st-manage-publication-channels-main-cta',
    imports: [MatButtonToggleModule, MatIcon, MatListModule, MatMenuModule],
    templateUrl: './manage-publication-channels-main-cta.component.html',
    styleUrl: './manage-publication-channels-main-cta.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-manage-publication-channels-main-cta',
    },
})
export class ManagePublicationChannelsMainCtaComponent implements OnInit {
    store = inject(Store);
    dialog = inject(MatDialog);
    externalActionsService = inject(ExternalActionsService);
    globalTablesService = inject(GlobalTablesService);

    private readonly defaultScheduleList: LookupValue[] = [{ id: 'Daily', title: 'Daily' }];

    readonly languageSettings = this.store.selectSignal(selectI18N)();

    public externalActionsList: Record<string, string>[] = [];
    public typePropertyList: LookupValue[] = [];
    public schedulePropertyList: LookupValue[] = [];
    public channelRevisionsList: { id: boolean; title: string }[] = [
        { id: true, title: 'Yes' },
        { id: false, title: 'No' },
    ];
    public propertyList: FieldDef[] = [];

    public ngOnInit() {
        this.externalActionsList = this.externalActionsService.getExternalActionsForType(ObjectTypes.PUBLICATION_CHANNEL).map((item: ExternalAction) => ({
            id: item.ID,
            title: item.Description,
        }));
        this.propertyList = this.globalTablesService.getDynamicPropertiesFor('PublicationChannel', '');
        this.typePropertyList = this.getListItems(PSTFieldNames.PSTPublicationChannelType, this.propertyList);
        this.schedulePropertyList = this.getListItems(PSTFieldNames.PSTPublicationChannelSchedule, this.propertyList) || this.defaultScheduleList;
    }

    public getListItems(pstFieldName: PSTFieldNames, propertyList?: FieldDef[]): LookupValue[] {
        const properties = propertyList || this.globalTablesService.getDynamicPropertiesFor('PublicationChannel', '');
        if (!properties) return [];
        const typeProperty = properties.find((item: FieldDef) => item.ID === pstFieldName);
        if (!typeProperty) return [];
        return this.globalTablesService.getListItems(typeProperty.ID, typeProperty.List);
    }

    async openNewChannelDialog() {
        this.dialog.closeAll();
        await this.dialog.afterAllClosed.pipe(take(1)).toPromise();
        const result = await this.dialog
            .open(AddPublicationChannelComponent, {
                width: '35rem',
                hasBackdrop: true,
                disableClose: true,
                panelClass: 'st-add-publication-channel-dialog',
                data: {
                    externalActionsList: this.externalActionsList,
                    typePropertyList: this.typePropertyList,
                    schedulePropertyList: this.schedulePropertyList,
                    channelRevisionsList: this.channelRevisionsList,
                },
            })
            .afterClosed()
            .toPromise();
        if (result) {
            this.store.dispatch(CastApiActions.loadPublicationsChannels());
        }
    }
}
